import ax, { AxiosRequestConfig } from "axios"
import { FormSection } from "../types/FormSection"
import { LanguageOption } from "../types/LanguageOption"
import { ReferralPayload } from "../types/ReferralPayload"
import { Referral } from "../types/Referral"
import { PaginationMeta } from "../types/PaginationMeta"
import { AdminMeta } from "../types/AdminMeta"
import { AuthService } from "./AuthService"
import { EnvironmentService } from "./EnvironmentService"
import { FormQuestionFormat } from "../types/FormQuestionFormat"

const axios = (language?: LanguageOption, isV2?: boolean) => {
  const apiVersion = isV2 ? "v2" : "v1"

  return ax.create({
    baseURL: language
      ? `${EnvironmentService.getApiBaseUrl()}/${language}/api/${apiVersion}`
      : `${EnvironmentService.getApiBaseUrl()}/api/${apiVersion}`,
  })
}

const getConfig = async (): Promise<AxiosRequestConfig> => {
  const token = await AuthService.getToken()
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
}

interface Database {
  getFormByProvince: (
    province: string,
    language?: LanguageOption,
  ) => Promise<FormSection[]>
  postReferralFormData: (
    province: string,
    referralData: ReferralPayload,
    language: LanguageOption,
  ) => Promise<Referral>
  getReferrals: (params?: {
    page?: number
    sort?: string
    filter?: { key: string; value: string }
  }) => Promise<{
    referrals: Array<Referral>
    pagination: PaginationMeta
  }>
  getReferralById: (
    id: string,
    language: LanguageOption,
    useVersion2?: boolean,
  ) => Promise<{
    referral: Referral
    form: FormSection[]
    referral_status: Array<{
      id: number
      label: Referral["status"]
      order: number
    }>
    admin: AdminMeta
  }>
  patchReferralFormData: (
    id: string,
    referralData: ReferralPayload,
    useVersion2?: boolean,
  ) => Promise<Referral>
  validateCsv: (
    file: File,
    province: string,
    language?: LanguageOption,
  ) => Promise<{
    message: string
  }>
  mapCsv: (
    file: File,
    province: string,
    language?: LanguageOption,
  ) => Promise<Referral>
  exportToTranquility: (id: string, useVersion2?: boolean) => Promise<null>
  submitOntarioReferralV2: (data: ReferralPayload) => Promise<any>
  getFormQuestionFormat: () => Promise<FormQuestionFormat[]>
}

const getReferrals: Database["getReferrals"] = async (
  { page, sort, filter } = { page: 1 },
) => {
  const config = await getConfig()
  const response = await axios().get(
    `/admin/referrals?page=${page ?? 1}${sort?.length ? "&sort=" + sort : ""}${
      filter && filter?.value?.length
        ? "&" + filter.key + "=" + filter.value
        : ""
    }`,
    config,
  )
  return response.data
}

const getReferralById: Database["getReferralById"] = async (
  id = "0",
  language,
  useVersion2,
) => {
  const config = await getConfig()
  let response

  // Don't make a request to the backend if v2 is undefined, it should only be true or false
  // This is to prevent the backend from returning a 404 when the user is on the referral review page
  if (useVersion2 === undefined) return

  if (useVersion2) {
    response = await axios(language, true).get(`/admin/referrals/${id}`, config)
  } else {
    response = await axios(language).get(`/admin/referrals/${id}`, config)
  }
  return response.data
}

const getFormByProvince: Database["getFormByProvince"] = async (
  province,
  language,
) => {
  const config = province.toLowerCase() === "ontario" ? await getConfig() : {}
  const response = await axios(language).get(
    `/referral_form/${province}`,
    config,
  )
  return response.data
}

const postReferralFormData: Database["postReferralFormData"] = async (
  province: string,
  referralData,
  language?: LanguageOption,
) => {
  const config = province.toLowerCase() === "ontario" ? await getConfig() : {}
  const response = await axios(language).post(
    `/referrals/${province}`,
    referralData,
    config,
  )
  return response.data
}

const validateCsv: Database["validateCsv"] = async (
  file: File,
  province: string,
  language?: LanguageOption,
) => {
  const config = province.toLowerCase() === "ontario" ? await getConfig() : {}
  const formData = new FormData()
  formData.append("file", file)
  const response = await axios(language).post(
    `/upload_csvs/${province}/validate`,
    formData,
    config,
  )
  return response.data
}

const mapCsv: Database["mapCsv"] = async (
  file: File,
  province: string,
  language?: LanguageOption,
) => {
  const config = province.toLowerCase() === "ontario" ? await getConfig() : {}
  const formData = new FormData()
  formData.append("file", file)
  const response = await axios(language).post(
    `/upload_csvs/${province}/mapping`,
    formData,
    config,
  )
  return response.data
}

const patchReferralFormData: Database["patchReferralFormData"] = async (
  id,
  referralData,
  useVersion2,
) => {
  const config = await getConfig()
  const response = await axios(undefined, useVersion2).patch(
    `/admin/referrals/${id}`,
    referralData,
    config,
  )
  return response.data
}

const exportToTranquility: Database["exportToTranquility"] = async (
  id,
  useVersion2,
) => {
  const config = await getConfig()
  if (useVersion2) {
    await axios(undefined, true).post(
      `/admin/referrals/${id}/sendToTranq`,
      {},
      config,
    )
    return null
  }
  await axios().post(`/admin/referrals/${id}/sendToTranq`, {}, config)
  return null
}

const submitOntarioReferralV2: Database["submitOntarioReferralV2"] = async (
  data: ReferralPayload,
) => {
  const config = await getConfig()
  return await axios("en", true).post("/referrals/ontario", data, config)
}

const getFormQuestionFormat: Database["getFormQuestionFormat"] = async () => {
  const config = await getConfig()
  const response = await axios("en", true).get(
    "/referrals/ontario/form_questions",
    config,
  )

  if (response.data.length) {
    return response.data
  }

  return []
}

export const DataBaseService: Database = {
  getFormByProvince,
  postReferralFormData,
  getReferrals,
  getReferralById,
  patchReferralFormData,
  validateCsv,
  mapCsv,
  exportToTranquility,
  submitOntarioReferralV2,
  getFormQuestionFormat,
}
